import {
    useState
} from "react";
import {
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    type ChartConfig,
    NCoreUIKitTheme,
    RadialChart,
    BarChart,
    TabBar,
    Button,
    Badge,
    Chart,
    Item,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    TrendingUp,
    Activity,
    Target,
    Share,
    Flame,
    Leaf,
    User
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Ring = {
    value: number;
    goal: number;
    unit: string;
    id: string;
};

const FlameIcon = createKitIcon(Flame);

const TrendingUpIcon = createKitIcon(TrendingUp);

const ActivityIcon = createKitIcon(Activity);

const ShareIcon = createKitIcon(Share);

const TargetIcon = createKitIcon(Target);

const UserIcon = createKitIcon(User);

const LeafIcon = createKitIcon(Leaf);

const RINGS: Array<Ring> = [
    {
        unit: "kcal",
        id: "move",
        value: 420,
        goal: 600
    },
    {
        id: "exercise",
        unit: "min",
        value: 32,
        goal: 45
    },
    {
        id: "stand",
        unit: "hr",
        value: 9,
        goal: 12
    }
];

const STEPS = [
    6420,
    8910,
    7350,
    9480,
    5870,
    10240,
    6980
];

const FIRST_DAY = new Date(2026, 9, 26);

const TODAY_INDEX = 6;

const STEP_GOAL = 8000;

const PERCENT = 100;

const RING_ORDER = [
    "stand",
    "exercise",
    "move"
];

const stylesheet = StyleSheet.create({
    section: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    footer: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    header: {
        justifyContent: "space-between",
        alignItems: "flex-end",
        flexDirection: "row"
    },
    ringLegend: {
        justifyContent: "space-between",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        flexDirection: "column",
        flex: 1
    },
    legendValue: {
        alignItems: "baseline",
        flexDirection: "row"
    },
    column: {
        flexDirection: "column"
    },
    workoutEnd: {
        alignItems: "flex-end"
    },
    ringsRow: {
        alignItems: "stretch"
    },
    legendTitle: {
        alignItems: "center",
        flexDirection: "row"
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    dot: {
        flexShrink: 0
    },
    legend: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const Fitness01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        tab,
        setTab
    ] = useState("today");

    const [
        activeIndex,
        setActiveIndex
    ] = useState<number | null>(TODAY_INDEX);

    const integer = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0
    });

    const decimal = new Intl.NumberFormat(activeLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });

    const percent = new Intl.NumberFormat(activeLocale, {
        style: "percent"
    });

    const dateText = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long",
        year: "numeric",
        day: "numeric",
        month: "long"
    }).format(new Date(2026, 10, 1));

    const dayFormat = new Intl.DateTimeFormat(activeLocale, {
        weekday: "short"
    });

    const average = STEPS.reduce((sum, value) => sum + value, 0) / STEPS.length;

    const stepConfig: ChartConfig = {
        steps: {
            title: t("blocks.fitness01.stepsTitle"),
            colorIndex: 1
        }
    };

    const stepRows = STEPS.map((steps, index) => ({
        day: dayFormat.format(new Date(FIRST_DAY.getFullYear(), FIRST_DAY.getMonth(), FIRST_DAY.getDate() + index)),
        steps
    }));

    const ringRows = RINGS.map((ring) => ({
        value: Math.round(ring.value / ring.goal * PERCENT),
        name: t(`blocks.fitness01.ring_${ring.id}`),
        id: ring.id
    })).sort((first, second) => RING_ORDER.indexOf(first.id) - RING_ORDER.indexOf(second.id));

    const getRingColor = (id: string) => colors.chart.categorical[String(RING_ORDER.indexOf(id) + 1) as keyof typeof colors.chart.categorical];

    const handleActiveChange = (index: number | undefined) => setActiveIndex(index ?? TODAY_INDEX);

    const activeSteps = STEPS[activeIndex ?? TODAY_INDEX] ?? 0;

    const renderLegend = (ring: Ring) => <View
        style={stylesheet.column}
        key={ring.id}
    >
        <View
            style={[
                stylesheet.legendTitle,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={[
                    stylesheet.dot,
                    {
                        backgroundColor: getRingColor(ring.id),
                        borderRadius: radiuses.full,
                        height: spaces.spacingSm,
                        width: spaces.spacingSm
                    }
                ]}
            />
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t(`blocks.fitness01.ring_${ring.id}`)}
            </Text>
        </View>
        <View
            style={[
                stylesheet.legendValue,
                {
                    gap: spaces.spacingXs
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
                color="high"
            >
                {integer.format(ring.value)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.fitness01.ringGoal", [
                    integer.format(ring.goal),
                    t(`blocks.fitness01.unit_${ring.unit}`)
                ])}
            </Text>
        </View>
    </View>;

    return <View
        style={stylesheet.page}
    >
        <ScrollView
            contentContainerStyle={[
                stylesheet.screen,
                {
                    maxWidth: shapeConfigs.sheetMaxWidth,
                    padding: spaces.spacingMd,
                    gap: spaces.spacingLg
                }
            ]}
            style={stylesheet.legend}
        >
            <View
                style={[
                    stylesheet.header,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={stylesheet.legend}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {dateText}
                    </Text>
                    <Text
                        variant="headlineMediumSize"
                        color="high"
                    >
                        {t("blocks.fitness01.title")}
                    </Text>
                </View>
                <Badge
                    title={t("blocks.fitness01.streak", [
                        integer.format(12)
                    ])}
                    variant="outline"
                    icon={FlameIcon}
                    type="neutral"
                />
            </View>
            <Card
                variant="outline"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.ringsRow,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View>
                            <RadialChart
                                valueFormatter={(value) => percent.format(value / PERCENT)}
                                categoryTitle={t("blocks.fitness01.ringsTitle")}
                                summary={t("blocks.fitness01.ringsSummary", [
                                    integer.format(420),
                                    integer.format(32),
                                    integer.format(9)
                                ])}
                                isAnimatedOnMount={true}
                                maxValue={PERCENT}
                                categoryKey="name"
                                plotSize="medium"
                                valueKey="value"
                                data={ringRows}
                                variant="bars"
                            />
                        </View>
                        <View
                            style={[
                                stylesheet.ringLegend,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {RINGS.map(renderLegend)}
                        </View>
                    </View>
                </Card.Content>
            </Card>
            <Chart
                footer={<View
                    style={stylesheet.footer}
                >
                    <View
                        style={[
                            stylesheet.legendTitle,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.fitness01.goal", [
                                integer.format(STEP_GOAL)
                            ])}
                        </Text>
                        <Badge
                            title={`+${percent.format(0.09)}`}
                            icon={TrendingUpIcon}
                            type="success"
                        />
                    </View>
                    <Text
                        variant="titleSmallSize"
                        color="high"
                    >
                        {t("blocks.fitness01.dayValue", [
                            stepRows[activeIndex ?? TODAY_INDEX]?.day ?? "",
                            integer.format(activeSteps)
                        ])}
                    </Text>
                </View>}
                description={t("blocks.fitness01.stepsAverage", [
                    integer.format(average)
                ])}
                title={t("blocks.fitness01.stepsTitle")}
                spreadBehaviour="stretch"
                isAnimatedOnMount={true}
                config={stepConfig}
            >
                <BarChart
                    valueFormatter={(value) => integer.format(value)}
                    categoryTitle={t("blocks.fitness01.day")}
                    onActiveChange={handleActiveChange}
                    categoryKey="day"
                    plotSize="small"
                    data={stepRows}
                    seriesKeys={[
                        "steps"
                    ]}
                />
            </Chart>
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={stylesheet.section}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {t("blocks.fitness01.workouts")}
                    </Text>
                    <Button
                        title={t("blocks.fitness01.all")}
                        onPress={handlePress}
                        variant="link"
                        type="neutral"
                        size="small"
                    />
                </View>
                <Item.Group
                    variant="outline"
                >
                    <Item
                        onPress={handlePress}
                    >
                        <Item.Media
                            icon={ActivityIcon}
                            variant="icon"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("blocks.fitness01.run")}
                            </Item.Title>
                            <Item.Description>
                                {t("blocks.fitness01.runMeta", [
                                    "07:15",
                                    integer.format(341)
                                ])}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <View
                                style={stylesheet.workoutEnd}
                            >
                                <Text
                                    variant="titleSmallSize"
                                    color="high"
                                >
                                    {t("blocks.fitness01.runDistance", [
                                        decimal.format(5.2)
                                    ])}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.fitness01.runRight", [
                                        "28:14",
                                        "5:26"
                                    ])}
                                </Text>
                            </View>
                        </Item.Actions>
                    </Item>
                    <Item
                        onPress={handlePress}
                    >
                        <Item.Media
                            icon={LeafIcon}
                            variant="icon"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("blocks.fitness01.yoga")}
                            </Item.Title>
                            <Item.Description>
                                {t("blocks.fitness01.yogaMeta", [
                                    "19:00",
                                    integer.format(142)
                                ])}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <View
                                style={stylesheet.workoutEnd}
                            >
                                <Text
                                    variant="titleSmallSize"
                                    color="high"
                                >
                                    {t("blocks.fitness01.yogaDuration", [
                                        integer.format(30)
                                    ])}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.fitness01.yogaKind")}
                                </Text>
                            </View>
                        </Item.Actions>
                    </Item>
                </Item.Group>
            </View>
        </ScrollView>
        <TabBar
            items={[
                {
                    title: t("blocks.fitness01.tabToday"),
                    icon: TargetIcon,
                    key: "today"
                },
                {
                    title: t("blocks.fitness01.tabWorkout"),
                    icon: ActivityIcon,
                    key: "workout"
                },
                {
                    title: t("blocks.fitness01.tabShare"),
                    icon: ShareIcon,
                    key: "share"
                },
                {
                    title: t("blocks.fitness01.tabProfile"),
                    icon: UserIcon,
                    key: "profile"
                }
            ]}
            title={t("blocks.fitness01.nav")}
            onChange={setTab}
            activeKey={tab}
        />
    </View>;
};
export default Fitness01;
